import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, MessageCircle } from "lucide-react";
import { formatErr, http, waLink } from "../../lib/api";
import { useFacets } from "./PartFinder";

export const RegSearch = () => {
  const nav = useNavigate();
  const facets = useFacets();
  const [reg, setReg] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!reg.trim()) return;
    setBusy(true);
    setMsg(null);
    try {
      const { data } = await http.get("/vehicle/lookup", { params: { reg } });
      if (data?.found && data?.make) {
        const make = facets.find((f) => f.make.toLowerCase() === String(data.make).toLowerCase())?.make ?? data.make;
        const p = new URLSearchParams({ make, reg: data.reg });
        if (data.year) p.set("year", data.year);
        if (data.colour) p.set("colour", data.colour);
        nav(`/shop?${p.toString()}`);
        return;
      }
      setMsg({ reg: data?.reg || reg.toUpperCase(), notFound: data?.configured && !data?.found });
    } catch (err) {
      setMsg({ reg: reg.toUpperCase(), error: formatErr(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.0, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="mb-3 border border-white/10 bg-[#121820]/70 p-5 backdrop-blur-xl sm:p-6"
      data-testid="reg-search"
    >
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-300">Quickest way</p>
          <p className="mt-1 font-display text-3xl font-extrabold uppercase leading-none sm:text-4xl">Search by reg</p>
        </div>
        <form onSubmit={submit} className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto">
          <label className="flex h-16 overflow-hidden rounded-md border-2 border-black bg-[#FFD500] shadow-[0_0_0_1px_rgba(255,213,0,0.3)] sm:w-[340px]">
            <span className="flex w-11 flex-col items-center justify-end bg-[#003399] pb-1.5 font-mono text-[11px] font-bold text-white">UK</span>
            <input
              data-testid="reg-search-input"
              value={reg}
              onChange={(e) => setReg(e.target.value.toUpperCase().slice(0, 9))}
              placeholder="AB12 CDE"
              aria-label="Vehicle registration"
              className="w-full bg-transparent px-4 text-center font-display text-4xl font-black uppercase tracking-[0.08em] text-black outline-none placeholder:text-black/30"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="reg-search-button"
            className="group flex h-16 items-center justify-center gap-2 rounded-md bg-white px-7 font-semibold text-[#090D12] transition-transform hover:scale-[1.03] active:scale-95 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
            Find my airbags
          </button>
        </form>
      </div>
      {msg && (
        <div data-testid="reg-search-message" className="mt-5 flex flex-col gap-3 border-t border-white/10 pt-5 text-sm text-slate-200 sm:flex-row sm:items-center sm:justify-between">
          <p>
            {msg.error
              ? msg.error
              : msg.notFound
                ? `We couldn't find ${msg.reg} on the DVLA database. Send it to us and we'll check by hand.`
                : `Send us ${msg.reg} and we'll reply with the airbags that fit your car.`}
          </p>
          {!msg.error && (
            <a
              data-testid="reg-search-whatsapp"
              href={waLink(`Hi CBLS AIRBAGS, which airbags do you have for my car? Reg: ${msg.reg}`)}
              target="_blank"
              rel="noreferrer"
              className="flex shrink-0 items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 font-semibold text-[#06281b] hover:bg-emerald-400"
            >
              <MessageCircle className="h-4 w-4" /> Send reg on WhatsApp
            </a>
          )}
        </div>
      )}
    </motion.div>
  );
};
